import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Edit, FileText, Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { Header } from "@/components/Header";
import { EditMeetingDialog } from "@/components/meetings/EditMeetingDialog";
import { MeetingList } from "@/components/meetings/MeetingList";
import { ScheduleMeetingDialog } from "@/components/meetings/ScheduleMeetingDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCustomerMeetings,
  useDeleteMeeting,
  useUpdateMeetingStatus,
} from "@/hooks/useCustomerMeetings";
import cloudflare from "@/integrations/cloudflare/client";
import type { Meeting } from "@/types/meeting.types";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postcode?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Project {
  id: number;
  project_name?: string;
  customer_name?: string;
  status: string;
  budget?: string;
  created_at: string;
}

export default function CustomerDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { data: customer, isLoading: customerLoading } = useQuery({
    queryKey: ["customer", id, refreshTrigger],
    queryFn: async () => {
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch customer");
      }
      const result = await response.json();
      return result.data as Customer;
    },
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["customer-projects", customer?.email, refreshTrigger],
    queryFn: async () => {
      if (!customer?.email) return [];
      const response = await fetch(
        `${cloudflare.apiUrl}/api/v1/projects?email=${encodeURIComponent(customer.email)}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }
      const result = await response.json();
      return result.data as Project[];
    },
    enabled: !!customer?.email,
  });

  // Fetch meetings for this customer
  const { data: meetings, isLoading: meetingsLoading } = useCustomerMeetings(Number(id));

  // Meeting mutations
  const deleteMeeting = useDeleteMeeting();
  const updateMeetingStatus = useUpdateMeetingStatus();

  const handleSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
    setIsEditDialogOpen(false);
  };

  const handleDeleteMeeting = async (meeting: Meeting) => {
    if (!confirm("Are you sure you want to delete this event?")) {
      return;
    }
    await deleteMeeting.mutateAsync({
      meetingId: meeting.id,
      customerId: meeting.customer_id,
    });
  };

  const handleCompleteMeeting = async (meeting: Meeting) => {
    await updateMeetingStatus.mutateAsync({
      meetingId: meeting.id,
      status: "completed",
    });
  };

  const handleCancelMeeting = async (meeting: Meeting) => {
    if (!confirm("Are you sure you want to cancel this event?")) {
      return;
    }
    await updateMeetingStatus.mutateAsync({
      meetingId: meeting.id,
      status: "cancelled",
    });
  };

  if (customerLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          onNewProject={() => navigate("/new-project")}
          onViewProjects={() => navigate("/projects")}
        />
        <main className="container mx-auto px-4 py-6">
          <div className="text-center">Loading customer details...</div>
        </main>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          onNewProject={() => navigate("/new-project")}
          onViewProjects={() => navigate("/projects")}
        />
        <main className="container mx-auto px-4 py-6">
          <div className="text-center">Customer not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />

      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate("/customers")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold">
                  {customer.first_name} {customer.last_name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Customer since {new Date(customer.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Button onClick={() => setIsEditDialogOpen(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Customer
            </Button>
          </div>

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{customer.email}</p>
                  </div>
                </div>

                {customer.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Phone</p>
                      <p className="text-sm text-muted-foreground">{customer.phone}</p>
                    </div>
                  </div>
                )}

                {(customer.address || customer.city) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Address</p>
                      <p className="text-sm text-muted-foreground">
                        {customer.address && (
                          <span>
                            {customer.address}
                            <br />
                          </span>
                        )}
                        {customer.city && <span>{customer.city}</span>}
                        {customer.state && <span>, {customer.state}</span>}
                        {customer.postcode && <span> {customer.postcode}</span>}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(customer.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {customer.notes && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Notes</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {customer.notes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Projects */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Projects ({projects?.length || 0})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/new-project?email=${encodeURIComponent(customer.email)}`)}
              >
                New Project
              </Button>
            </CardHeader>
            <CardContent>
              {projectsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading projects...</div>
              ) : projects && projects.length > 0 ? (
                <div className="space-y-3">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div>
                        <p className="font-medium">
                          {project.project_name || `Project #${project.id}`}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Created {new Date(project.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {project.budget && <p className="text-sm font-medium">{project.budget}</p>}
                        <Badge variant="outline">{project.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p>No projects yet</p>
                  <p className="text-sm mt-1">Create a project for this customer to get started</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Calendar */}
          <MeetingList
            meetings={meetings || []}
            isLoading={meetingsLoading}
            onSchedule={() => setIsScheduleMeetingOpen(true)}
            onEdit={(meeting) => setEditingMeeting(meeting)}
            onDelete={handleDeleteMeeting}
            onComplete={handleCompleteMeeting}
            onCancel={handleCancelMeeting}
          />
        </div>
      </main>

      {/* Edit Dialog */}
      <CustomerForm
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleSuccess}
        customer={customer}
      />

      {/* Schedule Meeting Dialog */}
      <ScheduleMeetingDialog
        isOpen={isScheduleMeetingOpen}
        onClose={() => setIsScheduleMeetingOpen(false)}
        customerId={Number(id)}
      />

      {/* Edit Meeting Dialog */}
      <EditMeetingDialog
        isOpen={!!editingMeeting}
        onClose={() => setEditingMeeting(null)}
        meeting={editingMeeting}
      />
    </div>
  );
}
